import React from 'react'
import { createBrowserRouter, RouteObject } from 'react-router'

import AppProvider from '@sputnik/AppProvider'
import AutoLoginPage from '@sputnik/pages/PreApplicationPages/AutoLoginPage'
import PostLoginPage from '@sputnik/pages/PreApplicationPages/PostLoginPage/PostLoginPage'
import SignInPage from '@sputnik/pages/PreApplicationPages/SignInPage'
import CodeVerification from '@sputnik/pages/PreApplicationPages/SignInPage/CodeVerification'
import EmailVerification from '@sputnik/pages/PreApplicationPages/SignInPage/EmailVerification'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import lazyRetry from '@sputnik/utils/lazyRetry'

import RequireAuth from './RequireAuth'

// Sign-in pages above stay statically imported: /auth is the landing route for
// nearly every session, so it ships in the main chunk. Every other flow below is
// split into a per-flow chunk (shared webpackChunkName merges a flow's pages into
// one chunk, so intra-flow navigation never fetches). The two likely post-login
// destinations are prefetched at idle priority.

const Dashboard = lazyRetry(
  () => import(/* webpackChunkName: "dashboard", webpackPrefetch: true */ '@sputnik/pages/DashboardPages/Dashboard'),
)

const CreditApplicationPage = lazyRetry(
  () => import(/* webpackChunkName: "credit-app", webpackPrefetch: true */ '@sputnik/pages/CreditApplicationPages'),
)
const NameAndOwnershipForm = lazyRetry(
  () => import(/* webpackChunkName: "credit-app" */ '@sputnik/pages/CreditApplicationPages/NameAndOwnershipForm'),
)
const BusinessAddressForm = lazyRetry(
  () => import(/* webpackChunkName: "credit-app" */ '@sputnik/pages/CreditApplicationPages/BusinessAddressForm'),
)
const FinancialDetailsForm = lazyRetry(
  () => import(/* webpackChunkName: "credit-app" */ '@sputnik/pages/CreditApplicationPages/FinancialDetailsForm'),
)
const ConnectBankAccountForm = lazyRetry(
  () => import(/* webpackChunkName: "credit-app" */ '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm'),
)
const PeopleInfoForm = lazyRetry(
  () => import(/* webpackChunkName: "credit-app" */ '@sputnik/pages/CreditApplicationPages/PeopleInfoForm'),
)
const ReviewForm = lazyRetry(
  () => import(/* webpackChunkName: "credit-app" */ '@sputnik/pages/CreditApplicationPages/ReviewForm'),
)

const SubmittingApplicationPollingPage = lazyRetry(
  () =>
    import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/SubmittingApplicationPollingPage'),
)
const AcceptingOfferPollingPage = lazyRetry(
  () => import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/AcceptingOfferPollingPage'),
)
const SubmissionError = lazyRetry(
  () => import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/SubmissionError'),
)
const NoUWOfferPage = lazyRetry(
  () => import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/NoUWOfferPage'),
)
const FrozenCredit = lazyRetry(
  () => import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/FrozenCredit'),
)
const WorkingOutDetails = lazyRetry(
  () => import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/WorkingOutDetails'),
)
const MigrationSuccess = lazyRetry(
  () => import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/MigrationSuccess'),
)
const EligibleOffersPage = lazyRetry(
  () => import(/* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/EligibleOffersPage'),
)
const StandardCreditLineOfferPage = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/StandardCreditLineOfferPage'
    ),
)
const VariableCreditLineOfferPage = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/VariableCreditLineOfferPage'
    ),
)
const PrepayOfferPage = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/PrepayOfferPage/PrepayOfferPage'
    ),
)
const OfferAcceptedPage = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "post-app" */ '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/OfferAcceptedPage'
    ),
)

const GoodFitSurveyPage = lazyRetry(
  () => import(/* webpackChunkName: "good-fit" */ '@sputnik/pages/PreApplicationPages/GoodFitSurveyPage'),
)
const GoodFitResultPage = lazyRetry(
  () =>
    import(/* webpackChunkName: "good-fit" */ '@sputnik/pages/PreApplicationPages/GoodFitSurveyPage/GoodFitResultPage'),
)
const NotGoodFitResult = lazyRetry(
  () =>
    import(/* webpackChunkName: "good-fit" */ '@sputnik/pages/PreApplicationPages/GoodFitSurveyPage/NotGoodFitResult'),
)

const MobileDocumentUpload = lazyRetry(
  () => import(/* webpackChunkName: "mobile-doc-upload" */ '@sputnik/pages/MobileDocumentUpload/MobileDocumentUpload'),
)

const BdcLanding = lazyRetry(() => import(/* webpackChunkName: "misc-pages" */ '@sputnik/pages/BdcLanding'))
const GetToKnowYouPage = lazyRetry(
  () => import(/* webpackChunkName: "misc-pages" */ '@sputnik/pages/PreApplicationPages/GetToKnowYouPage'),
)
const NotFound = lazyRetry(() => import(/* webpackChunkName: "misc-pages" */ '@sputnik/pages/NotFound'))

const createSignInRoutes = (): RouteObject[] => {
  return [
    {
      path: PATHNAME_CONSTS.AUTH_PATH_ABSOLUTE,
      element: <SignInPage />,
      children: [
        {
          index: true,
          element: <EmailVerification />,
        },
        {
          path: PATHNAME_CONSTS.VERIFY_PATH,
          element: <CodeVerification />,
        },
      ],
    },
    {
      path: PATHNAME_CONSTS.MIGRATION_PATH_ABSOLUTE,
      element: <SignInPage />,
      children: [
        {
          index: true,
          element: <EmailVerification />,
        },
        {
          path: PATHNAME_CONSTS.VERIFY_PATH,
          element: <CodeVerification />,
        },
      ],
    },
    {
      path: PATHNAME_CONSTS.VERIFY_EMAIL_PATH,
      element: <AutoLoginPage />,
    },
    {
      path: PATHNAME_CONSTS.VERIFY_CODE_PATH,
      element: <AutoLoginPage />,
    },
    {
      path: PATHNAME_CONSTS.POST_LOGIN_PATH,
      element: <PostLoginPage />,
    },
  ]
}

export const createGoodFitSurveyRoutes = (): RouteObject[] => {
  return [
    {
      path: PATHNAME_CONSTS.GOOD_FIT_SURVEY_PATH,
      children: [
        {
          index: true,
          element: <GoodFitSurveyPage />,
        },
        {
          path: PATHNAME_CONSTS.GOOD_FIT_RESULT_PATH,
          element: <GoodFitResultPage />,
        },
        {
          path: PATHNAME_CONSTS.NOT_GOOD_FIT_RESULT_PATH,
          element: <NotGoodFitResult />,
        },
      ],
    },
  ]
}

export const createDashboardRoutes = (): RouteObject[] => {
  return [
    {
      path: '/',
      element: <Dashboard />,
    },
    {
      path: PATHNAME_CONSTS.DASHBOARD_PATH,
      element: <Dashboard />,
    },
  ]
}

const createCreditApplicationRoutes = (): RouteObject[] => {
  return [
    /* routes inside the credit application form */
    {
      path: 'app/:appId',
      element: <CreditApplicationPage />,
      children: [
        {
          path: PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH,
          element: <NameAndOwnershipForm />,
        },
        {
          path: PATHNAME_CONSTS.BUSINESS_ADDRESS_PATH,
          element: <BusinessAddressForm />,
        },
        {
          path: PATHNAME_CONSTS.FINANCIAL_DETAILS_PATH,
          element: <FinancialDetailsForm />,
        },
        {
          path: PATHNAME_CONSTS.CONNECT_BANK_ACCOUNT_PATH,
          element: <ConnectBankAccountForm />,
        },
        {
          path: PATHNAME_CONSTS.PEOPLE_INFO_PATH,
          element: <PeopleInfoForm />,
        },
        {
          path: PATHNAME_CONSTS.REVIEW_PATH,
          element: <ReviewForm />,
        },
      ],
    },
    /* routes after submission: polling pages, offer pages, and offer result pages */
    {
      path: 'app/:appId',
      children: [
        {
          path: PATHNAME_CONSTS.SUBMITTING_APPLICATION,
          element: <SubmittingApplicationPollingPage />,
        },
        {
          path: PATHNAME_CONSTS.ACCEPTING_OFFER, // COM-9188: update designs for this component
          element: <AcceptingOfferPollingPage />,
        },
        {
          path: PATHNAME_CONSTS.SUBMISSION_ERROR_PATH,
          element: <SubmissionError />,
        },
        {
          path: PATHNAME_CONSTS.NO_OFFER_PATH,
          element: <NoUWOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.FROZEN_CREDIT_PATH,
          element: <FrozenCredit />,
        },
        {
          path: PATHNAME_CONSTS.WORKING_OUT_DETAILS_PATH,
          element: <WorkingOutDetails />,
        },
        {
          path: PATHNAME_CONSTS.MIGRATION_SUCCESS_PATH,
          element: <MigrationSuccess />,
        },
      ],
    },
    {
      path: `app/:appId/${PATHNAME_CONSTS.OFFERS_PATH}`,
      children: [
        { element: <EligibleOffersPage />, index: true },
        {
          path: PATHNAME_CONSTS.STANDARD_CREDIT_LINE_PATH,
          element: <StandardCreditLineOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.VARIABLE_CREDIT_LINE_PATH,
          element: <VariableCreditLineOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.PREPAY_OFFER_PATH,
          element: <PrepayOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.OFFER_ACCEPTED_PATH,
          element: <OfferAcceptedPage />,
        },
      ],
    },
  ]
}

const createAuthenticatedRoutes = (): RouteObject[] => {
  return [
    {
      element: <RequireAuth />,
      children: [
        ...createDashboardRoutes(),
        ...createGoodFitSurveyRoutes(),
        ...createCreditApplicationRoutes(),
        {
          path: PATHNAME_CONSTS.MOBILE_DOCUMENT_UPLOAD_PATH,
          element: <MobileDocumentUpload />,
        },
        {
          path: PATHNAME_CONSTS.GET_TO_KNOW_YOU,
          element: <GetToKnowYouPage />,
        },
      ],
    },
  ]
}

export const createApplicationRoutes = () => {
  return createBrowserRouter([
    {
      element: <AppProvider />,
      children: [
        ...createSignInRoutes(),
        ...createAuthenticatedRoutes(),
        {
          path: PATHNAME_CONSTS.BDC_LANDING_PATH,
          element: <BdcLanding />,
        },
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ])
}
